import { create } from 'zustand';
import type { Project, Scene } from '../types';

interface ProjectState {
  projects: Project[];
  activeSceneId: string | null;
  activeProjectId: string | null;
  setProjects: (projects: Project[]) => void;
  addProject: (project: Project) => void;
  updateProject: (projectId: string, updater: (project: Project) => Project) => void;
  addSceneToProject: (projectId: string, scene: Scene) => void;
  setActiveScene: (sceneId: string | null) => void;
  setActiveProject: (projectId: string | null) => void;
  getActiveScene: () => Scene | null;
  getActiveProject: () => Project | null;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  activeSceneId: null,
  activeProjectId: null,

  setProjects: (projects) => set({ projects }),

  addProject: (project) =>
    set((state) => ({
      projects: [...state.projects, project],
      activeProjectId: project.id,
    })),

  updateProject: (projectId, updater) =>
    set((state) => ({
      projects: state.projects.map((p) => (p.id === projectId ? updater(p) : p)),
    })),

  addSceneToProject: (projectId, scene) =>
    set((state) => ({
      projects: state.projects.map((p) =>
        p.id === projectId
          ? { ...p, scenes: [...p.scenes, scene], updatedAt: new Date().toISOString() }
          : p
      ),
      activeSceneId: scene.id,
    })),

  setActiveScene: (sceneId) => set({ activeSceneId: sceneId }),

  setActiveProject: (projectId) => set({ activeProjectId: projectId }),

  getActiveScene: () => {
    const state = get();
    for (const project of state.projects) {
      const scene = project.scenes.find((s) => s.id === state.activeSceneId);
      if (scene) return scene;
    }
    return null;
  },

  getActiveProject: () => {
    const state = get();
    return state.projects.find((p) => p.id === state.activeProjectId) || null;
  },
}));
